import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useExitImpersonation } from 'api/users/hooks';
import { useAuthStore } from 'stores/authStore';

const getExpiryMs = (expiresAt) => {
  if (!expiresAt) return null;
  const end = new Date(expiresAt).getTime();
  return Number.isNaN(end) ? null : end;
};

const computeMinutesLeft = (expiresAt) => {
  const end = getExpiryMs(expiresAt);
  if (end === null) return null;
  const msLeft = end - Date.now();
  if (msLeft <= 0) return 0;
  return Math.floor(msLeft / 60000);
};

const useSessionMinutesLeft = (expiresAt) => {
  const [minutesLeft, setMinutesLeft] = useState(() => computeMinutesLeft(expiresAt));

  useEffect(() => {
    setMinutesLeft(computeMinutesLeft(expiresAt));
    const interval = setInterval(() => setMinutesLeft(computeMinutesLeft(expiresAt)), 60 * 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return minutesLeft;
};

const ImpersonationBanner = () => {
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const userEmail = useAuthStore((state) => state.user?.email);
  const expiresAt = useAuthStore((state) => state.user?.impersonated_by?.expires_at);
  const { mutate: exitImpersonation } = useExitImpersonation();
  const minutesLeft = useSessionMinutesLeft(expiresAt);

  useEffect(() => {
    if (!isImpersonating) return;

    const end = getExpiryMs(expiresAt);
    if (end === null) return;

    const exitIfExpired = () => {
      if (Date.now() >= end) {
        exitImpersonation();
      }
    };

    exitIfExpired();
    const interval = setInterval(exitIfExpired, 1000);
    return () => clearInterval(interval);
  }, [isImpersonating, expiresAt, exitImpersonation]);

  if (!isImpersonating) return null;

  const timeLabel = minutesLeft === null ? null : minutesLeft > 0 ? `${minutesLeft}m` : '<1m';

  return (
    <Overlay>
      viewing as {userEmail}
      {timeLabel && ` · ${timeLabel}`}
      {' · '}
      <ExitButton onClick={() => exitImpersonation()} title="Return to your superadmin account">
        switch back
      </ExitButton>
    </Overlay>
  );
};

export default ImpersonationBanner;

const Overlay = styled.div`
  position: fixed;
  top: 6px;
  right: 8px;
  font-size: 11px;
  font-family: monospace;
  color: #000;
  background: rgba(0, 0, 0, 0.08);
  padding: 4px 8px;
  border-radius: 4px;
  opacity: 0.5;
  pointer-events: none;
  z-index: 9999;
  user-select: none;
`;

const ExitButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;
  font-family: monospace;
  color: #000;
  cursor: pointer;
  pointer-events: all;

  &:hover {
    text-decoration: underline;
  }
`;
